import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { 
  Return, 
  ReturnStatus, 
  ReturnReason 
} from '../../../models/return.models';
import { ReturnsService } from '../../../services/returns.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-detalle-devolucion',
  templateUrl: './detalle-devolucion.component.html',
  styleUrls: ['./detalle-devolucion.component.css']
})
export class DetalleDevolucionComponent implements OnInit {

  returnData: Return | null = null;
  isLoading = true;
  returnId: number;

  // Traducciones para estados
  statusLabels = {
    [ReturnStatus.PENDING]: 'Pendiente',
    [ReturnStatus.APPROVED]: 'Aprobada',
    [ReturnStatus.REJECTED]: 'Rechazada',
    [ReturnStatus.PROCESSED]: 'Procesada'
  };

  // Traducciones para motivos
  reasonLabels = {
    [ReturnReason.DEFECTIVE]: 'Producto defectuoso',
    [ReturnReason.WRONG_ITEM]: 'Producto incorrecto',
    [ReturnReason.NOT_AS_DESCRIBED]: 'No coincide con descripción',
    [ReturnReason.CUSTOMER_CHANGED_MIND]: 'Cliente cambió de opinión',
    [ReturnReason.DAMAGED_IN_SHIPPING]: 'Dañado en envío',
    [ReturnReason.OTHER]: 'Otros'
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private returnsService: ReturnsService
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.returnId = +params['id'];
      this.loadReturn();
    });
  }

  /**
   * Carga los datos de la devolución
   */
  loadReturn(): void {
    this.isLoading = true;
    
    this.returnsService.getReturn(this.returnId).subscribe({
      next: (returnData) => {
        this.returnData = returnData;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading return:', error);
        Swal.fire('Error', 'Error al cargar la devolución', 'error');
        this.router.navigate(['/devoluciones']);
      }
    });
  }

  /**
   * Vuelve a la lista de devoluciones
   */
  goBack(): void {
    this.router.navigate(['/devoluciones']);
  }

  /**
   * Navega a editar devolución
   */
  editReturn(): void {
    if (this.returnData && this.canPerformAction('edit')) {
      this.router.navigate(['/devoluciones/editar', this.returnData.id]);
    }
  }

  /**
   * Aprueba la devolución
   */
  approveReturn(): void {
    if (!this.returnData || !this.canPerformAction('approve')) return;

    Swal.fire({
      title: 'Aprobar Devolución',
      text: `¿Está seguro que desea aprobar la devolución ${this.returnData.returnNumber}?`,
      input: 'textarea',
      inputPlaceholder: 'Notas internas (opcional)',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Aprobar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed && this.returnData) {
        this.returnsService.approveReturn(this.returnData.id, result.value).subscribe({
          next: (updatedReturn) => {
            this.returnData = updatedReturn;
            Swal.fire('Éxito', 'Devolución aprobada exitosamente', 'success');
          },
          error: (error) => {
            console.error('Error approving return:', error);
            Swal.fire('Error', 'Error al aprobar la devolución', 'error');
          }
        });
      }
    });
  }

  /**
   * Rechaza la devolución
   */
  rejectReturn(): void {
    if (!this.returnData || !this.canPerformAction('reject')) return;

    Swal.fire({
      title: 'Rechazar Devolución',
      text: `¿Está seguro que desea rechazar la devolución ${this.returnData.returnNumber}?`,
      input: 'textarea',
      inputPlaceholder: 'Motivo del rechazo (requerido)',
      inputValidator: (value) => {
        if (!value) {
          return 'Debe especificar un motivo para el rechazo'
        }
        return null;
      },
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Rechazar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed && this.returnData) {
        this.returnsService.rejectReturn(this.returnData.id, result.value).subscribe({
          next: (updatedReturn) => {
            this.returnData = updatedReturn;
            Swal.fire('Rechazada', 'Devolución rechazada', 'success');
          },
          error: (error) => {
            console.error('Error rejecting return:', error);
            Swal.fire('Error', 'Error al rechazar la devolución', 'error');
          }
        });
      }
    });
  }

  /**
   * Procesa la devolución
   */
  processReturn(): void {
    if (!this.returnData || !this.canPerformAction('process')) return;

    Swal.fire({
      title: 'Procesar Devolución',
      text: `¿Está seguro que desea procesar la devolución ${this.returnData.returnNumber}? Esta acción actualizará el inventario y no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Procesar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed && this.returnData) {
        this.returnsService.processReturn(this.returnData.id).subscribe({
          next: (updatedReturn) => {
            this.returnData = updatedReturn;
            Swal.fire('Éxito', 'Devolución procesada exitosamente', 'success');
          },
          error: (error) => {
            console.error('Error processing return:', error);
            Swal.fire('Error', 'Error al procesar la devolución', 'error');
          }
        });
      }
    });
  }

  /**
   * Elimina la devolución
   */
  deleteReturn(): void {
    if (!this.returnData || !this.canPerformAction('delete')) return;

    Swal.fire({
      title: 'Eliminar Devolución',
      text: `¿Está seguro que desea eliminar la devolución ${this.returnData.returnNumber}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed && this.returnData) {
        this.returnsService.deleteReturn(this.returnData.id).subscribe({
          next: () => {
            Swal.fire('Eliminada', 'Devolución eliminada exitosamente', 'success');
            this.router.navigate(['/devoluciones']);
          },
          error: (error) => {
            console.error('Error deleting return:', error);
            Swal.fire('Error', 'Error al eliminar la devolución', 'error');
          }
        });
      }
    });
  }

  /**
   * Verifica si una acción está disponible según el estado
   */
  canPerformAction(action: string): boolean {
    if (!this.returnData) return false;

    switch (action) {
      case 'edit':
      case 'delete':
        return this.returnData.status === ReturnStatus.PENDING;
      case 'approve':
      case 'reject':
        return this.returnData.status === ReturnStatus.PENDING;
      case 'process':
        return this.returnData.status === ReturnStatus.APPROVED;
      default:
        return false;
    }
  }

  /**
   * Obtiene la clase CSS para el estado
   */
  getStatusClass(status: ReturnStatus): string {
    switch (status) {
      case ReturnStatus.PENDING:
        return 'status-pending';
      case ReturnStatus.APPROVED:
        return 'status-approved';
      case ReturnStatus.REJECTED:
        return 'status-rejected';
      case ReturnStatus.PROCESSED:
        return 'status-processed';
      default:
        return '';
    }
  }

  /**
   * Calcula el total de la devolución
   */
  calculateTotal(): number {
    if (!this.returnData || !this.returnData.details) return 0;
    
    return this.returnData.details.reduce((total, detail) => {
      return total + (detail.totalPrice || 0);
    }, 0);
  }

  /**
   * Navega al comprobante original
   */
  viewOriginalVoucher(): void {
    if (this.returnData?.originalVoucher) {
      // Navegar al detalle del comprobante original
      // La implementación depende de cómo esté estructurado el routing de comprobantes
      this.router.navigate(['/comprobante', 'view', this.returnData.originalVoucher.id]);
    }
  }

  /**
   * Imprime la devolución
   */
  printReturn(): void {
    // Implementar funcionalidad de impresión
    window.print();
  }
}